import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { TEMPLATE_CODE_DESCRIPTION } from "lib/components/builders/constants";
import ReportingUnitsStep, { type ReportingUnitConfig } from "lib/components/builders/files/ReportingUnitsStep";
import { EXTERNAL_ID_OVERVIEW_MESSAGE } from "lib/constants/messages";
import { putMasterPlan } from "lib/storage/masterPlanStorage";
import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import cx from "ui/cx";
import Checkbox from "ui/forms/Checkbox";
import InputGroupNumber from "ui/forms/InputGroupNumber";
import InputGroupText from "ui/forms/InputGroupText";
import useAppContext from "ui/hooks/useAppContext";
import AdminModal from "../admin/builders/AdminModal";
import styles from "./EditMasterPlanModal.module.scss";

interface EditMasterPlanModalProps {
  onClose: () => void;
  masterPlan: ClientMasterPlan;
  onSuccess: ((mp: ClientMasterPlan) => void) | undefined;
}

type FormState = Pick<
  ClientMasterPlan,
  | "name"
  | "externalId"
  | "description"
  | "templateCode"
  | "basePrice"
  | "disableShowroomViewer"
  | "disableShowroomInteriorView"
  | "planDownloadEnabled"
>;

const initialState: FormState = {
  name: "",
  externalId: "",
  description: "",
  templateCode: "",
  basePrice: 0,
  disableShowroomViewer: false,
  disableShowroomInteriorView: false,
  planDownloadEnabled: true,
};

type Tab = "details" | "reportingUnits";

function getReportingUnitsValidationError(units: ReportingUnitConfig[]): string | null {
  const seenNames = new Set<string>();

  for (const unit of units) {
    const normalizedName = unit.name.trim().toLowerCase();
    if (!normalizedName) {
      return "Each unit name is required.";
    }

    if (seenNames.has(normalizedName)) {
      return "Unit names must be unique.";
    }

    seenNames.add(normalizedName);
  }

  return null;
}

export default function EditMasterPlanModal({ onClose, onSuccess, masterPlan }: EditMasterPlanModalProps) {
  const [activeTab, setActiveTab] = useState<Tab>("details");
  const [form, setForm] = useState(initialState);
  const [reportingUnits, setReportingUnits] = useState<ReportingUnitConfig[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const {
      basePrice,
      externalId,
      description,
      name,
      templateCode,
      disableShowroomViewer,
      disableShowroomInteriorView,
      planDownloadEnabled,
    } = masterPlan;
    setForm({
      name,
      externalId,
      basePrice,
      description,
      templateCode,
      disableShowroomViewer,
      disableShowroomInteriorView,
      planDownloadEnabled,
    });

    // Initialize reporting units from master plan (convert null to empty string for form inputs)
    if (masterPlan.reportingUnits) {
      setReportingUnits(
        masterPlan.reportingUnits.map((ru) => ({
          reportingUnitId: ru.reportingUnitId,
          name: ru.name,
          externalId: ru.externalId || "",
          sortIndex: ru.sortIndex,
        })),
      );
    }
  }, [masterPlan]);

  const { setUserNotification } = useAppContext();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const reportingUnitsValidationError = getReportingUnitsValidationError(reportingUnits);
    if (reportingUnitsValidationError) {
      setActiveTab("reportingUnits");
      setUserNotification({
        messageType: "error",
        message: reportingUnitsValidationError,
      });
      return;
    }

    setIsSaving(true);

    const {
      basePrice,
      description,
      name,
      externalId,
      templateCode,
      disableShowroomViewer,
      disableShowroomInteriorView,
      planDownloadEnabled,
    } = form;

    // Prepare reporting units for submission (convert empty strings to undefined for API)
    const reportingUnitsPayload =
      reportingUnits.length > 0
        ? reportingUnits.map((ru) => ({
            reportingUnitId: ru.reportingUnitId,
            name: ru.name,
            externalId: ru.externalId || undefined,
            sortIndex: ru.sortIndex,
          }))
        : undefined;

    const [newMasterPlan, err] = await putMasterPlan(masterPlan.builderId, masterPlan.id, {
      ...(basePrice !== masterPlan.basePrice ? { basePrice } : {}),
      description: description ?? undefined,
      name: name ?? undefined,
      externalId,
      templateCode: templateCode ?? undefined,
      disableShowroomViewer,
      disableShowroomInteriorView,
      planDownloadEnabled,
      reportingUnits: reportingUnitsPayload,
    });

    if (err) {
      setUserNotification(err);
      setIsSaving(false);
      return;
    }

    assertIsNotUndefined(newMasterPlan);
    onSuccess?.(newMasterPlan);
    setUserNotification({
      messageType: "success",
      message: "Master Plan updated successfully",
    });
    setIsSaving(false);
    onClose();
  };

  const onChange = (key: string, value: string | number | boolean) => setForm({ ...form, [key]: value });

  const hasReportingUnits = reportingUnits.length > 0;

  return (
    <AdminModal
      onClose={onClose}
      onSubmit={handleSubmit}
      headerText="Edit Master Plan"
      disabled={isSaving}
      submitButtonText={isSaving ? "Saving..." : "Submit"}
      scrollModal={activeTab === "reportingUnits"}
    >
      {/* Tab Navigation - only show if there are reporting units */}
      {hasReportingUnits && (
        <div className={styles.tabNavigation}>
          <button
            type="button"
            onClick={() => setActiveTab("details")}
            className={cx(styles.tabButton, activeTab === "details" && styles.active)}
          >
            Details
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("reportingUnits")}
            className={cx(styles.tabButton, activeTab === "reportingUnits" && styles.active)}
            data-testid="edit-master-plan-units-tab"
          >
            Units {reportingUnits.length > 0 && `(${reportingUnits.length})`}
          </button>
        </div>
      )}

      {/* Details Tab */}
      {(!hasReportingUnits || activeTab === "details") && (
        <>
          <InputGroupText
            type="text"
            placeholder="Enter Name"
            onChange={(value) => onChange("name", value)}
            inputValue={form.name}
            labelText="Name"
            maxLength={100}
          />
          <div className="pt-4">
            <InputGroupNumber
              placeholder="Enter Base Price"
              labelText="Base Price"
              inputValue={form.basePrice}
              onChange={(value) => onChange("basePrice", value ?? 0)}
            />
          </div>
          <div className="pt-4">
            <InputGroupText
              type="textarea"
              placeholder="Enter Description"
              onChange={(value) => onChange("description", value)}
              inputValue={form.description}
              labelText="Description"
              maxLength={300}
            />
          </div>
          <div className="pt-4 two-column">
            <InputGroupText
              tooltip={EXTERNAL_ID_OVERVIEW_MESSAGE}
              type="text"
              placeholder="Enter External ID"
              onChange={(value) => onChange("externalId", value)}
              inputValue={form.externalId}
              labelText="External ID"
            />{" "}
            <div className="pt-4">
              <InputGroupText
                tooltip={TEMPLATE_CODE_DESCRIPTION("Master Plan")}
                type="text"
                placeholder="Enter Template Code"
                onChange={(value) => onChange("templateCode", value)}
                inputValue={form.templateCode}
                labelText="Template Code"
                maxLength={300}
              />
            </div>
          </div>
          <div className={styles.checkboxWrapper}>
            <Checkbox
              data-testid="disable-showroom-interior-view"
              label="Disable interior view for Showroom"
              checked={form.disableShowroomInteriorView}
              onChangeBoolean={(checked) => onChange("disableShowroomInteriorView", checked)}
            />
          </div>
          <div className={styles.checkboxWrapper}>
            <Checkbox
              label="Disable 3D plan view for Showroom Public Links"
              checked={form.disableShowroomViewer}
              onChangeBoolean={(checked) => onChange("disableShowroomViewer", checked)}
            />
          </div>
          <div className={styles.checkboxWrapper}>
            <Checkbox
              label="Enable Plan Download"
              checked={form.planDownloadEnabled}
              onChangeBoolean={(checked) => onChange("planDownloadEnabled", checked)}
            />
          </div>
        </>
      )}

      {/* Units Tab */}
      {hasReportingUnits && activeTab === "reportingUnits" && (
        <div className={styles.reportingUnitsTab}>
          <ReportingUnitsStep reportingUnits={reportingUnits} onReportingUnitsChange={setReportingUnits} />
        </div>
      )}
    </AdminModal>
  );
}
